/**
 * The Phase 5G operations: governance policies, exceptions, recorded
 * evaluations, settings, compliance evidence and governance insights.
 *
 * Split from `operations.ts` for length and re-exported from it, as the other
 * phases are. One function per `/v1` operation, paths checked against the
 * generated contract. Enforcement is not an operation: the server applies the
 * policies at the merge, release and deployment boundaries, and nothing in the
 * browser decides what a policy allows.
 */

import { expect, expectNoContent, http } from './client';
import type { components } from './gen/schema';

type Schemas = components['schemas'];

export type Policy = Schemas['Policy'];
export type PolicyDetail = Schemas['PolicyDetail'];
export type PolicyActivation = Schemas['PolicyActivation'];
export type PolicyVersion = Schemas['PolicyVersion'];
export type PolicyRule = Schemas['PolicyRule'];
export type PolicyRuleKind = Schemas['PolicyRuleKind'];
export type PolicyCreate = Schemas['PolicyCreate'];
export type PolicyDraftSave = Schemas['PolicyDraftSave'];
export type PolicyMode = Schemas['PolicyMode'];
export type PolicyTarget = Schemas['PolicyTarget'];
export type PolicyResult = Schemas['PolicyResult'];
export type PolicyEffect = Schemas['PolicyEffect'];
export type PolicyException = Schemas['PolicyException'];
export type PolicyExceptionDecision = Schemas['PolicyExceptionDecision'];
export type PolicyEvaluation = Schemas['PolicyEvaluation'];
export type PolicyDecision = Schemas['PolicyDecision'];
export type GovernanceSettings = Schemas['GovernanceSettings'];
export type GovernanceInsights = Schemas['GovernanceInsights'];
export type GovernancePosture = Schemas['GovernancePosture'];
export type GovernanceIntegrity = Schemas['GovernanceIntegrity'];
export type ComplianceReport = Schemas['ComplianceReport'];
export type ComplianceReportSummary = Schemas['ComplianceReportSummary'];
export type ComplianceControl = Schemas['ComplianceControl'];
export type InsightsTrendPoint = Schemas['InsightsTrendPoint'];

export interface PagedGovernance<T> {
  data: T[];
  next_cursor: string | null;
}

function paged<T>(value: { data?: unknown; next_cursor?: unknown }): PagedGovernance<T> {
  return {
    data: (value.data ?? []) as T[],
    next_cursor: typeof value.next_cursor === 'string' ? value.next_cursor : null,
  };
}

const orgPath = (orgId: string) => ({ org_id: orgId });
const policyPath = (orgId: string, policyId: string) => ({ org_id: orgId, policy_id: policyId });
const exceptionPath = (orgId: string, exceptionId: string) => ({
  org_id: orgId,
  exception_id: exceptionId,
});

export async function listPolicyRuleKinds(orgId: string): Promise<PolicyRuleKind[]> {
  const value = await expect(
    http.GET('/organizations/{org_id}/governance/rule-kinds', { params: { path: orgPath(orgId) } }),
  );
  return value.data;
}

export interface PoliciesQuery {
  target?: PolicyTarget;
  scope?: 'organization' | 'repository';
  repository_id?: string;
  active?: boolean;
  q?: string;
  limit?: number;
  cursor?: string;
}

export async function listPolicies(
  orgId: string,
  query: PoliciesQuery = {},
): Promise<PagedGovernance<Policy>> {
  const value = await expect(
    http.GET('/organizations/{org_id}/governance/policies', {
      params: { path: orgPath(orgId), query },
    }),
  );
  return paged<Policy>(value);
}

export function createPolicy(orgId: string, body: PolicyCreate): Promise<PolicyDetail> {
  return expect(
    http.POST('/organizations/{org_id}/governance/policies', {
      params: { path: orgPath(orgId) },
      body,
    }),
  );
}

export function getPolicy(orgId: string, policyId: string): Promise<PolicyDetail> {
  return expect(
    http.GET('/organizations/{org_id}/governance/policies/{policy_id}', {
      params: { path: policyPath(orgId, policyId) },
    }),
  );
}

export function deletePolicy(orgId: string, policyId: string): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/governance/policies/{policy_id}', {
      params: { path: policyPath(orgId, policyId) },
    }),
  );
}

export function savePolicyDraft(
  orgId: string,
  policyId: string,
  body: PolicyDraftSave,
): Promise<PolicyDetail> {
  return expect(
    http.PUT('/organizations/{org_id}/governance/policies/{policy_id}/draft', {
      params: { path: policyPath(orgId, policyId) },
      body,
    }),
  );
}

export function activatePolicy(
  orgId: string,
  policyId: string,
  ifVersion?: number,
): Promise<PolicyActivation> {
  return expect(
    http.POST('/organizations/{org_id}/governance/policies/{policy_id}/activate', {
      params: { path: policyPath(orgId, policyId) },
      body: ifVersion === undefined ? {} : { if_version: ifVersion },
    }),
  );
}

export function disablePolicy(
  orgId: string,
  policyId: string,
  reason: string,
): Promise<PolicyDetail> {
  return expect(
    http.POST('/organizations/{org_id}/governance/policies/{policy_id}/disable', {
      params: { path: policyPath(orgId, policyId) },
      body: { reason },
    }),
  );
}

export function diffPolicyVersions(
  orgId: string,
  policyId: string,
  from?: number,
  to?: number,
): Promise<Record<string, unknown>> {
  return expect(
    http.GET('/organizations/{org_id}/governance/policies/{policy_id}/diff', {
      params: {
        path: policyPath(orgId, policyId),
        query: {
          ...(from === undefined ? {} : { from }),
          ...(to === undefined ? {} : { to }),
        },
      },
    }),
  );
}

export function getGovernanceSettings(orgId: string): Promise<GovernanceSettings> {
  return expect(
    http.GET('/organizations/{org_id}/governance/settings', { params: { path: orgPath(orgId) } }),
  );
}

export function updateGovernanceSettings(
  orgId: string,
  body: Schemas['GovernanceSettingsUpdate'],
): Promise<GovernanceSettings> {
  return expect(
    http.PATCH('/organizations/{org_id}/governance/settings', {
      params: { path: orgPath(orgId) },
      body,
    }),
  );
}

export interface ExceptionsQuery {
  repository_id?: string;
  policy_id?: string;
  status?:
    'requested' | 'approved' | 'rejected' | 'revoked' | 'remediated' | 'expired' | 'in_force';
  limit?: number;
  cursor?: string;
}

export async function listPolicyExceptions(
  orgId: string,
  query: ExceptionsQuery = {},
): Promise<PagedGovernance<PolicyException>> {
  const value = await expect(
    http.GET('/organizations/{org_id}/governance/exceptions', {
      params: { path: orgPath(orgId), query },
    }),
  );
  return paged<PolicyException>(value);
}

export function decidePolicyException(
  orgId: string,
  exceptionId: string,
  body: PolicyExceptionDecision,
): Promise<PolicyException> {
  return expect(
    http.POST('/organizations/{org_id}/governance/exceptions/{exception_id}/decision', {
      params: { path: exceptionPath(orgId, exceptionId) },
      body,
    }),
  );
}

export function revokePolicyException(
  orgId: string,
  exceptionId: string,
  reason: string,
): Promise<PolicyException> {
  return expect(
    http.POST('/organizations/{org_id}/governance/exceptions/{exception_id}/revoke', {
      params: { path: exceptionPath(orgId, exceptionId) },
      body: { reason },
    }),
  );
}

export interface EvaluationsQuery {
  repository_id?: string;
  policy_id?: string;
  target?: PolicyTarget;
  result?: PolicyResult;
  effect?: PolicyEffect;
  since?: string;
  until?: string;
  limit?: number;
  cursor?: string;
}

export async function listPolicyEvaluations(
  orgId: string,
  query: EvaluationsQuery = {},
): Promise<PagedGovernance<PolicyEvaluation>> {
  const value = await expect(
    http.GET('/organizations/{org_id}/governance/evaluations', {
      params: { path: orgPath(orgId), query },
    }),
  );
  return paged<PolicyEvaluation>(value);
}

export interface GovernanceInsightsQuery {
  since?: string;
  until?: string;
  repository_id?: string;
}

export function getGovernanceInsights(
  orgId: string,
  query: GovernanceInsightsQuery = {},
): Promise<GovernanceInsights> {
  return expect(
    http.GET('/organizations/{org_id}/governance/insights', {
      params: { path: orgPath(orgId), query },
    }),
  );
}

export function getGovernancePosture(orgId: string): Promise<GovernancePosture> {
  return expect(
    http.GET('/organizations/{org_id}/governance/posture', { params: { path: orgPath(orgId) } }),
  );
}

export function getGovernanceIntegrity(orgId: string): Promise<GovernanceIntegrity> {
  return expect(
    http.GET('/organizations/{org_id}/governance/integrity', { params: { path: orgPath(orgId) } }),
  );
}

export async function listComplianceReports(orgId: string): Promise<ComplianceReportSummary[]> {
  const value = await expect(
    http.GET('/organizations/{org_id}/governance/compliance/reports', {
      params: { path: orgPath(orgId), query: {} },
    }),
  );
  return paged<ComplianceReportSummary>(value).data;
}

export function generateComplianceReport(
  orgId: string,
  body: Schemas['ComplianceReportRequest'],
  idempotencyKey?: string,
): Promise<ComplianceReport> {
  return expect(
    http.POST('/organizations/{org_id}/governance/compliance/reports', {
      params: {
        path: orgPath(orgId),
        ...(idempotencyKey ? { header: { 'Idempotency-Key': idempotencyKey } } : {}),
      },
      body,
    } as never),
  );
}

export function getComplianceReport(orgId: string, reportId: string): Promise<ComplianceReport> {
  return expect(
    http.GET('/organizations/{org_id}/governance/compliance/reports/{report_id}', {
      params: { path: { org_id: orgId, report_id: reportId } },
    }),
  );
}

/** The exported file, as the server rendered it. Audited server-side. */
export async function exportComplianceReport(
  orgId: string,
  reportId: string,
  format: 'json' | 'csv',
): Promise<Blob> {
  return expect(
    http.GET('/organizations/{org_id}/governance/compliance/reports/{report_id}/export', {
      params: { path: { org_id: orgId, report_id: reportId }, query: { format } },
      parseAs: 'blob',
    } as never),
  );
}

export async function getOrganizationInsightsTrend(
  orgId: string,
  query: { since?: string; until?: string; environment?: string; bucket_days?: number } = {},
): Promise<InsightsTrendPoint[]> {
  const value = await expect(
    http.GET('/organizations/{org_id}/insights/trend', {
      params: { path: orgPath(orgId), query },
    }),
  );
  return value.data;
}
